import { ChevronDown, CircleUser, LogOut } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import { useAuthActions, useSession } from "../auth.ts";
import { useSiteCopy } from "../i18n/useSiteCopy.ts";
import { loginPath } from "../routing.ts";
import { ButtonLink } from "../ui/Button.tsx";
import { cn } from "../ui/cn.ts";
import { toast } from "../ui/toast.tsx";
import type { SiteNavLink } from "./nav.ts";

const itemClass = cn(
  "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-base",
  "text-card-foreground hover:bg-muted",
);

interface AccountMenuProps {
  /** Already filtered to what the signed-in visitor may see. */
  links: SiteNavLink[];
}

/** Sign-in button, or the signed-in name with a disclosure menu (site links, sign out). */
export const AccountMenu = ({ links }: AccountMenuProps): ReactNode => {
  const copy = useSiteCopy().account;
  const { user, loading } = useSession();
  const { signOut } = useAuthActions();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  // Close on navigation: adjust during render instead of an effect (no extra commit).
  const [menuPath, setMenuPath] = useState(location.pathname);
  if (menuPath !== location.pathname) {
    setMenuPath(location.pathname);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return (): void => {
      document.removeEventListener("pointerdown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleSignOut = async (): Promise<void> => {
    setSigningOut(true);
    try {
      await signOut();
      setOpen(false);
      toast.success(copy.signedOut);
    } finally {
      setSigningOut(false);
    }
  };

  if (loading) return <span className="inline-block h-10 w-24" aria-hidden="true" />;

  if (!user) {
    if (location.pathname === "/inloggen") return null;
    return (
      <ButtonLink
        to={loginPath(`${location.pathname}${location.search}`)}
        size="sm"
        className="bg-header-foreground text-header hover:bg-highlight">
        {copy.signIn}
      </ButtonLink>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={copy.menu(user.name)}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          "inline-flex min-h-11 max-w-[11rem] items-center gap-1.5 rounded-full border",
          "border-header-foreground/35 px-3 text-base font-semibold hover:border-highlight",
          "sm:max-w-[16rem]",
        )}>
        <CircleUser aria-hidden="true" className="size-5 shrink-0" />
        {/* Below sm the button is icon-only (aria-label names the user) so the brand fits. */}
        <span className="hidden truncate sm:block">{user.name}</span>
        <ChevronDown
          aria-hidden="true"
          className={cn("size-4 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>
      <div
        id={menuId}
        hidden={!open}
        className={cn(
          "absolute right-0 z-40 mt-2 w-64 rounded-xl border border-border bg-card p-2",
          "text-card-foreground shadow-xl",
        )}>
        <p className="px-3 pt-1 pb-2 text-base text-muted-foreground">
          {copy.signedInAs}
          <span className="block truncate font-semibold text-card-foreground">{user.email}</span>
        </p>
        <ul className="border-t border-border pt-2">
          {links.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) => cn(itemClass, isActive && "bg-muted font-semibold")}>
                {link.icon && <link.icon aria-hidden="true" className="size-5 text-primary" />}
                {link.label}
              </NavLink>
            </li>
          ))}
          <li>
            <button
              type="button"
              disabled={signingOut}
              className={itemClass}
              onClick={() => void handleSignOut()}>
              <LogOut aria-hidden="true" className="size-5 text-primary" />
              {copy.signOut}
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};
